import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ShieldCheck, Sparkles, Heart, Sunrise } from "lucide-react";
import LiveTrackDemo from "@/components/LiveTrackDemo";
import { BrandMark } from "@/components/Brand";

// Brand palette — purple + orange (matches the Delco Chefs logo)
const BRAND_PURPLE = "#7c3aed";
const BRAND_ORANGE = "#f97316";

const HIGHLIGHTS = [
  {
    icon: ShieldCheck,
    title: "Reliability",
    blurb: "Verified home chefs, on time, every day. No surprises.",
    color: BRAND_PURPLE,
    bg: "#ede9fe",
  },
  {
    icon: Heart,
    title: "Trust",
    blurb: "Background-checked, badged, and accountable — every chef.",
    color: BRAND_ORANGE,
    bg: "#ffedd5",
  },
  {
    icon: Sparkles,
    title: "Quality",
    blurb: "Fresh, seasonal, prepared in your kitchen the way you like it.",
    color: BRAND_PURPLE,
    bg: "#ede9fe",
  },
  {
    icon: Sunrise,
    title: "Daily Convenience",
    blurb: "Morning chai to dinner — a single tap, every single day.",
    color: BRAND_ORANGE,
    bg: "#ffedd5",
  },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (loading || !user) return;
    if (user.role === "admin") nav("/admin", { replace: true });
    else if (user.role === "chef") nav("/chef", { replace: true });
    else nav(user.onboarded ? "/app" : "/onboarding", { replace: true });
  }, [user, loading, nav]);

  return (
    <div className="bg-white text-[#1d1d1f] min-h-screen">
      {/* Minimal sticky nav — logo only, no wordmark */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/[0.06]">
        <div className="max-w-6xl mx-auto h-14 px-6 flex items-center justify-between">
          <Link to="/" data-testid="brand"><BrandMark size={32}/></Link>
          <div className="flex items-center gap-5 text-[13px] text-[#1d1d1f]/80">
            <Link to="/preview" className="hover:text-[#1d1d1f]" data-testid="nav-preview">Preview</Link>
            <Link to="/customer" className="hover:text-[#1d1d1f]" data-testid="nav-login">Sign in</Link>
            <Link
              to="/customer"
              className="text-white px-3 py-1.5 rounded-full text-[12px] font-medium transition-all hover:scale-[1.02]"
              style={{ background: `linear-gradient(135deg, ${BRAND_PURPLE}, ${BRAND_ORANGE})` }}
              data-testid="nav-get-started"
            >
              Get started
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO — new headline + purple+orange gradient */}
      <section className="pt-20 pb-12 sm:pt-32 sm:pb-20 px-6 text-center">
        <p className="text-sm sm:text-base text-[#1d1d1f]/60 mb-3 font-medium tracking-wide" data-testid="hero-eyebrow">
          Noida · Home meals on autopilot
        </p>
        <h1
          className="font-bold tracking-[-0.04em] leading-[0.95]"
          style={{ fontSize: "clamp(48px, 9vw, 112px)" }}
          data-testid="hero-title"
        >
          Convenience.<br/>
          <span
            style={{ background: `linear-gradient(135deg, ${BRAND_PURPLE}, ${BRAND_ORANGE})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", color: "transparent" }}
          >
            Personified.
          </span>
        </h1>
        <p className="max-w-2xl mx-auto mt-6 sm:mt-8 text-lg sm:text-2xl text-[#1d1d1f]/70 leading-tight tracking-tight" data-testid="hero-sub">
          A verified home chef. Your kitchen. Daily.
        </p>
        <div className="mt-10 sm:mt-12 flex items-center justify-center gap-4 flex-wrap text-[15px]">
          <Link
            to="/customer"
            className="text-white px-7 py-3 rounded-full font-medium transition-all hover:scale-[1.02]"
            style={{ background: `linear-gradient(135deg, ${BRAND_PURPLE}, ${BRAND_ORANGE})` }}
            data-testid="hero-get-started"
          >
            Get started
          </Link>
          <Link
            to="/customer"
            className="font-medium inline-flex items-center gap-1 group"
            style={{ color: BRAND_PURPLE }}
            data-testid="hero-login"
          >
            Sign in <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5"/>
          </Link>
        </div>
      </section>

      {/* ANIMATED MAP DEMO */}
      <section className="px-6">
        <div className="max-w-7xl mx-auto" data-testid="hero-image">
          <LiveTrackDemo />
        </div>
      </section>

      {/* 4 HIGHLIGHTS — Reliability / Trust / Quality / Daily Convenience */}
      <section className="px-6 py-20 sm:py-32 max-w-6xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {HIGHLIGHTS.map(({ icon: Icon, title, blurb, color, bg }, i) => (
            <div
              key={i}
              className="rounded-3xl bg-[#faf7ff] p-8 sm:p-10 flex flex-col items-start hover:shadow-md transition-shadow"
              data-testid={`highlight-${i}`}
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: bg }}>
                <Icon size={22} strokeWidth={2.2} style={{ color }}/>
              </div>
              <h3 className="font-bold tracking-tight leading-tight" style={{ fontSize: "clamp(22px, 2.4vw, 28px)", color: BRAND_PURPLE }}>
                {title}
              </h3>
              <p className="mt-3 text-[15px] text-[#1d1d1f]/65 leading-snug">{blurb}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA — Get started + Sign in REMOVED per spec; keep tagline only */}
      <section className="px-6 py-20 sm:py-28 max-w-4xl mx-auto text-center">
        <h2
          className="font-bold tracking-[-0.04em] leading-[1.02] mb-3"
          style={{ fontSize: "clamp(36px, 5.5vw, 72px)",
                   background: `linear-gradient(135deg, ${BRAND_PURPLE}, ${BRAND_ORANGE})`,
                   WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                   backgroundClip: "text", color: "transparent" }}
        >
          Ready when you are.
        </h2>
        <p className="text-[15px] text-[#1d1d1f]/60">Pick a service. We&rsquo;ll handle the rest.</p>
      </section>

      {/* Minimal footer — logo only */}
      <footer className="border-t border-black/[0.06] py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <BrandMark size={28}/>
          <p className="text-xs text-[#1d1d1f]/50">© {new Date().getFullYear()} · Noida, India</p>
        </div>
      </footer>
    </div>
  );
}
